// SFCC-11240 (ARTICLES-13) — pure breadcrumb-trail logic for the
// LearningCenterArticleBreadcrumb block, split out so it's framework-free and
// unit-testable (and to satisfy react-refresh/only-export-components on the
// component file). Mirrors hec-composable's `learning-center/utils.js`
// (`addStaticCrumbs`, `articlesPageData`) + the article breadcrumb's per-state
// crumb construction.

export type ArticleBreadcrumbState = "article" | "article-topic" | null;

export type Crumb = { label: string; href?: string };

/**
 * SFCC-11723 — localized static-crumb labels formatted by the component (via
 * `react-intl`) and threaded in so this module stays framework-free and
 * unit-testable.
 */
export interface ArticleTrailLabels {
  home: string;
  learningCenter: string;
  articles: string;
}

const ARTICLES_HREF = "/learning-center/articles";

/** URL pathname → breadcrumb state (mirrors composable path parsing). */
export function resolveArticleBreadcrumbState(
  pathname: string,
): ArticleBreadcrumbState {
  const paths = pathname.split("/").filter(Boolean);
  const last = paths[paths.length - 1] ?? null;
  if (last === "articles") return "article-topic";
  if (last && paths.includes("articles")) return "article";
  return null;
}

/**
 * Build the breadcrumb trail. Mirrors the composable: `addStaticCrumbs` prepends
 * Home + HSA Learning Center, then the per-state tail. A crumb with an `href` is
 * a link; without one it's the current (non-clickable) page. Returns `[]` when
 * there's nothing to render (no matching state, or an article page whose title
 * hasn't resolved yet).
 */
export function buildArticleBreadcrumbTrail({
  state,
  articleTitle,
  topicTitle,
  labels,
}: {
  state: ArticleBreadcrumbState;
  articleTitle?: string | null;
  topicTitle?: string | null;
  /** Localized static-crumb labels (see `ArticleTrailLabels`). */
  labels: ArticleTrailLabels;
}): Crumb[] {
  const withStatic = (tail: Crumb[]): Crumb[] => [
    { label: labels.home, href: "/" },
    { label: labels.learningCenter, href: "/learning-center" },
    ...tail,
  ];

  if (state === "article") {
    if (!articleTitle) return [];
    return withStatic([
      { label: labels.articles, href: ARTICLES_HREF },
      { label: articleTitle },
    ]);
  }

  if (state === "article-topic") {
    if (topicTitle) {
      return withStatic([
        { label: labels.articles, href: ARTICLES_HREF },
        { label: topicTitle },
      ]);
    }
    // No topic title → `Articles` is the current (non-clickable) crumb.
    return withStatic([{ label: labels.articles }]);
  }

  return [];
}
